import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Linkedin, Twitter, Instagram, Facebook, ArrowUpRight } from 'lucide-react';
import { SERVICES } from '@/lib/services';
import { trackCTA } from '@/lib/gtm';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_c422f3aa-3782-49d4-8e3c-f15c34e2c74c/artifacts/halfbrs0_wizbang.png";

const footerLinks = {
  company: [
    { name: "About Us", path: "/about" },
    { name: "Our Work", path: "/work" },
    { name: "Our Clients", path: "/clients" },
    { name: "Blog", path: "/blog" },
    { name: "Careers", path: "/careers" },
    { name: "FAQ", path: "/faq" },
  ],
  support: [
    { name: "Contact", path: "/contact" },
    { name: "Book Appointment", path: "/book-appointment" },
    { name: "Free Strategy Call", path: "/free-strategy" },
  ],
};

const socialLinks = [
  { icon: Linkedin, href: "#", label: "LinkedIn" },
  { icon: Twitter, href: "#", label: "Twitter" },
  { icon: Instagram, href: "#", label: "Instagram" },
  { icon: Facebook, href: "#", label: "Facebook" },
];

export default function Footer() {
  return (
    <footer className="dark-section text-white relative overflow-hidden" data-testid="footer">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-30 blur-3xl" style={{ background: 'radial-gradient(circle, #ff5a1f, transparent)' }} />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 relative">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-12 mb-16">
          {/* Brand Column */}
          <div className="lg:col-span-4">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="Wizbang" className="h-10 w-auto" />
              <span className="font-syne text-2xl font-bold"></span>
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-8 max-w-md">
              A <span className="font-editorial text-[#ff5a1f]">creative</span>, <span className="font-editorial text-[#ff5a1f]">media</span> and <span className="font-editorial text-[#ff5a1f]">technology</span> agency building revenue systems for ambitious brands.
            </p>

            <div className="space-y-3">
              <a href="tel:9878780298" className="flex items-center gap-3 text-white/70 hover:text-[#ff5a1f] transition-colors text-sm">
                <Phone className="w-4 h-4" />
                <span>+91 98787 80298</span>
              </a>
              <a href="mailto:sayhi@wizbang.in" className="flex items-center gap-3 text-white/70 hover:text-[#ff5a1f] transition-colors text-sm">
                <Mail className="w-4 h-4" />
                <span>sayhi@wizbang.in</span>
              </a>
              <span className="flex items-center gap-3 text-white/70 text-sm">
                <MapPin className="w-4 h-4" />
                <span>www.wizbang.in · India</span>
              </span>
            </div>
          </div>

          {/* Services Links */}
          <div className="lg:col-span-3">
            <h4 className="font-syne font-bold text-white mb-6 uppercase tracking-wider text-xs">Services</h4>
            <ul className="space-y-3">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-white/60 hover:text-[#ff5a1f] transition-colors text-sm link-underline">
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Links */}
          <div className="lg:col-span-2">
            <h4 className="font-syne font-bold text-white mb-6 uppercase tracking-wider text-xs">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-white/60 hover:text-[#ff5a1f] transition-colors text-sm link-underline">{link.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div className="lg:col-span-3">
            <h4 className="font-syne font-bold text-white mb-6 uppercase tracking-wider text-xs">Get in touch</h4>
            <ul className="space-y-3 mb-6">
              {footerLinks.support.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-white/60 hover:text-[#ff5a1f] transition-colors text-sm link-underline">{link.name}</Link>
                </li>
              ))}
            </ul>
            <Link to="/book-appointment" data-testid="footer-cta" onClick={() => trackCTA('footer_start_project', 'footer')}
              className="inline-flex items-center gap-2 bg-[#ff5a1f] text-white px-5 py-3 text-sm font-semibold rounded-full hover:bg-white hover:text-[#0A0A0A] transition-colors">
              Start a project
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* Big wordmark */}
        <div className="border-t border-white/10 pt-12 pb-8">
          <h2 className="font-syne text-[18vw] sm:text-[16vw] lg:text-[14vw] font-bold leading-none tracking-tighter text-white/5 select-none">
            WIZBANG
          </h2>
        </div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 border-t border-white/10 pt-6">
          <p className="text-white/40 text-xs">
            © {new Date().getFullYear()} Wizbang. Creative · Media · Technology Agency.
          </p>
          <div className="flex items-center gap-4">
            {socialLinks.map((social) => (
              <a key={social.label} href={social.href} aria-label={social.label}
                className="p-2 text-white/40 hover:text-[#ff5a1f] transition-colors">
                <social.icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
